// <story-zigzag> — scroll reveal + SVG elbow connectors between alternating cards.
// Plain HTMLElement, no shadow DOM. Connectors are measured in JS so they track
// the real card positions across breakpoints; CSS handles the reveal animations.

const SVG_NS = 'http://www.w3.org/2000/svg';

class StoryZigzag extends HTMLElement {
  #controller = new AbortController();
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {HTMLElement | null} */
  #flow = null;
  /** @type {SVGSVGElement | null} */
  #svg = null;
  /** @type {HTMLElement[]} */
  #frames = [];
  #revealed = false;
  #reducedMotion = false;
  #rafPending = false;

  connectedCallback() {
    this.#reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#flow = this.querySelector('[data-sz-flow]');
    this.#svg = this.querySelector('[data-sz-svg]');
    this.#frames = Array.from(this.querySelectorAll('[data-sz-frame]'));

    this.#observeReveal();
    this.#buildConnectors();

    const { signal } = this.#controller;
    window.addEventListener('resize', this.#onResize, { passive: true, signal });

    // Recompute once images have loaded (aspect-ratio is fixed, but be safe).
    this.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', this.#onResize, { signal });
        img.addEventListener('error', this.#onResize, { signal });
      }
    });
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    this.#controller.abort();
  }

  #observeReveal() {
    if (!('IntersectionObserver' in window)) {
      this.#reveal();
      return;
    }

    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.12) {
            this.#reveal();
            this.#io?.disconnect();
            this.#io = null;
            return;
          }
        }
      },
      { threshold: [0, 0.12, 0.25] }
    );

    this.#io.observe(this);
  }

  #reveal() {
    this.#revealed = true;
    this.classList.add('is-revealed');
    this.#drawIn();
  }

  #onResize = () => {
    if (this.#rafPending) return;
    this.#rafPending = true;
    requestAnimationFrame(() => {
      this.#rafPending = false;
      this.#buildConnectors();
    });
  };

  // ===== Connectors =====

  #buildConnectors() {
    if (!this.#flow || !this.#svg) return;
    if (!this.classList.contains('story-zigzag--connected')) return;
    if (this.#frames.length < 2) return;

    // Connectors are hidden on mobile (CSS), so skip the work there.
    if (matchMedia('(max-width: 749px)').matches) {
      this.#svg.replaceChildren();
      return;
    }

    const flowRect = this.#flow.getBoundingClientRect();
    const w = this.#flow.clientWidth;
    const h = this.#flow.clientHeight;
    if (w === 0 || h === 0) return;

    this.#svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    this.#svg.replaceChildren();

    const center = (el) => {
      const r = el.getBoundingClientRect();
      return {
        x: r.left + r.width / 2 - flowRect.left,
        bottom: r.bottom - flowRect.top,
        top: r.top - flowRect.top,
      };
    };

    for (let i = 0; i < this.#frames.length - 1; i++) {
      const a = center(this.#frames[i]);
      const b = center(this.#frames[i + 1]);

      const sx = a.x;
      const sy = a.bottom;
      const ex = b.x;
      const ey = b.top;
      const midY = (sy + ey) / 2;

      const dir = ex >= sx ? 1 : -1;
      // Corner radius clamped so it never overshoots a short segment.
      const r = Math.max(4, Math.min(20, Math.abs(ex - sx) / 2, Math.abs(ey - sy) / 2));

      const d = [
        `M ${sx} ${sy}`,
        `L ${sx} ${midY - r}`,
        `Q ${sx} ${midY} ${sx + dir * r} ${midY}`,
        `L ${ex - dir * r} ${midY}`,
        `Q ${ex} ${midY} ${ex} ${midY + r}`,
        `L ${ex} ${ey}`,
      ].join(' ');

      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('class', 'sz-conn-path');
      path.setAttribute('d', d);

      const len = path.getTotalLength();
      path.style.strokeDasharray = `${len}`;
      // If already revealed (e.g. resize after scroll), draw instantly.
      if (this.#revealed || this.#reducedMotion) {
        path.style.strokeDashoffset = '0';
      } else {
        path.style.strokeDashoffset = `${len}`;
        path.style.transition = `stroke-dashoffset 820ms cubic-bezier(0.2, 0.7, 0.1, 1) ${i * 130 + 200}ms`;
      }
      this.#svg.appendChild(path);

      // Nodes at both ends.
      for (const [nx, ny] of [[sx, sy], [ex, ey]]) {
        const node = document.createElementNS(SVG_NS, 'circle');
        node.setAttribute('class', 'sz-conn-node');
        node.setAttribute('cx', `${nx}`);
        node.setAttribute('cy', `${ny}`);
        node.setAttribute('r', '5');
        node.style.transitionDelay = `${i * 130 + 320}ms`;
        this.#svg.appendChild(node);
      }
    }
  }

  // Kick the dash-offset transition once revealed.
  #drawIn() {
    if (!this.#svg) return;
    this.#svg.querySelectorAll('.sz-conn-path').forEach((p) => {
      /** @type {SVGPathElement} */ (p).style.strokeDashoffset = '0';
    });
  }
}

if (!customElements.get('story-zigzag')) {
  customElements.define('story-zigzag', StoryZigzag);
}
